import React, { useState } from 'react';
import { api } from '../utils/api';
import { 
  Settings as SettingsIcon, Bell, MessageSquare, Phone, Send, 
  Eye, Clock, CheckCircle, AlertCircle, Database, Download, RefreshCw, HardDrive
} from 'lucide-react';
import toast from 'react-hot-toast';

interface BackupFile {
  filename: string;
  size: number;
  createdAt: string;
}

const Settings: React.FC = () => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:7070/api';
  
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [reportPreview, setReportPreview] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'whatsapp' | 'email'>('email');

  const [backups, setBackups] = useState<BackupFile[]>([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [triggerBackupLoading, setTriggerBackupLoading] = useState(false);

  React.useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    setBackupsLoading(true);
    try {
      const response = await fetch(`${API_URL}/backups`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setBackups(data);
      }
    } catch (error) {
      console.error('Error fetching backups:', error);
    } finally {
      setBackupsLoading(false);
    }
  };

  const handleTriggerBackup = async () => {
    setTriggerBackupLoading(true);
    try {
      const response = await fetch(`${API_URL}/backups/trigger`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        toast.success('Backup completed successfully!');
        fetchBackups();
      } else {
        toast.error('Failed to create backup.');
      }
    } catch (error) {
      toast.error('Error triggering backup.');
    } finally {
      setTriggerBackupLoading(false);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleTestReport = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/reports/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success('Test report sent successfully! Check your WhatsApp.');
      } else {
        toast.error('Failed to send test report. Check server configuration.');
      }
    } catch (error) {
      toast.error('Error sending test report. Check server connection.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewReport = async () => {
    setPreviewLoading(true);
    try {
      const response = await fetch(`${API_URL}/reports/preview`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReportPreview(data.message);
        setShowPreview(true);
      } else {
        toast.error('Failed to generate report preview');
      }
    } catch (error) {
      toast.error('Error generating preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleTestEmailReport = async () => {
    setEmailLoading(true);
    try {
      const response = await fetch(`${API_URL}/reports/email/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success('Test email report sent successfully! Check your email.');
      } else {
        toast.error('Failed to send test email report. Check server configuration.');
      }
    } catch (error) {
      toast.error('Error sending test email report. Check server connection.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handlePreviewEmailReport = async () => {
    setEmailPreviewLoading(true);
    try {
      const response = await fetch(`${API_URL}/reports/email/preview`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const htmlContent = await response.text();
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(htmlContent);
          newWindow.document.close();
        }
      } else {
        toast.error('Failed to generate email report preview');
      }
    } catch (error) {
      toast.error('Error generating email preview');
    } finally {
      setEmailPreviewLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <SettingsIcon className="h-7 w-7 mr-3 text-orange-600" />
            Settings
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Configure daily reports and system settings
          </p>
        </div>
      </div>

      {/* Daily Reports Section */}
      <div className="card p-6">
        <div className="flex items-center mb-6">
          <Bell className="h-6 w-6 mr-3 text-orange-600" />
          <h2 className="text-xl font-semibold text-gray-900">Daily Reports</h2>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('email')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'email'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📧 Email Reports (No Account Needed)
          </button>
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'whatsapp'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            💬 WhatsApp Reports (Requires Twilio)
          </button>
        </div>

        <div className="space-y-6">
          {activeTab === 'email' ? (
            <>
              {/* Email Feature Description */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-green-800 mb-1">
                      ✅ NO ACCOUNT NEEDED - Email Reports
                    </h3>
                    <p className="text-sm text-green-700">
                      Get beautiful HTML email reports sent to your inbox every day at 10:00 PM. 
                      Uses your existing Gmail - no third-party accounts required! Just enable 2-step verification and create an App Password.
                    </p>
                  </div>
                </div>
              </div>

              {/* Email Setup Steps */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Super Simple Setup (2 minutes)</h3>
                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex items-start">
                    <span className="bg-green-100 text-green-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium mr-3 flex-shrink-0 mt-0.5">1</span>
                    <div>
                      <p className="font-medium text-gray-700">Enable 2-Step Verification</p>
                      <p>Go to <span className="font-mono bg-gray-100 px-1 rounded">myaccount.google.com/security</span> → Enable 2-Step Verification</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <span className="bg-green-100 text-green-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium mr-3 flex-shrink-0 mt-0.5">2</span>
                    <div>
                      <p className="font-medium text-gray-700">Create App Password</p>
                      <p>In same page → App passwords → Generate → Select "Mail" → Copy the 16-character password</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <span className="bg-green-100 text-green-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium mr-3 flex-shrink-0 mt-0.5">3</span>
                    <div>
                      <p className="font-medium text-gray-700">Add to Environment Variables</p>
                      <p>Add your Gmail and App Password to <span className="font-mono bg-gray-100 px-1 rounded">.env</span> file</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handlePreviewEmailReport}
                  disabled={emailPreviewLoading}
                  className="btn-secondary flex items-center justify-center"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {emailPreviewLoading ? 'Generating...' : 'Preview Email Report'}
                </button>
                
                <button
                  onClick={handleTestEmailReport}
                  disabled={emailLoading}
                  className="btn-primary flex items-center justify-center"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {emailLoading ? 'Sending...' : 'Send Test Email'}
                </button>
              </div>

              {/* Email Environment Variables */}
              <div className="bg-gray-900 rounded-lg p-4 text-sm text-green-400 font-mono overflow-x-auto">
                <div className="text-gray-400 mb-2"># Add these to your server/.env file:</div>
                <div>GMAIL_USER=your-email@gmail.com</div>
                <div>GMAIL_APP_PASSWORD=your_16_character_app_password</div>
                <div>OWNER_EMAIL=owner-email@gmail.com</div>
              </div>
            </>
          ) : (
            <>
              {/* WhatsApp Feature Description */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <MessageSquare className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-blue-800 mb-1">
                      Automated WhatsApp Reports
                    </h3>
                    <p className="text-sm text-blue-700">
                      Get daily business summaries sent directly to your WhatsApp every day at 10:00 PM. 
                      Includes sales data, cash received, transactions, and pending reminders.
                    </p>
                  </div>
                </div>
              </div>

          {/* Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-sm font-medium text-green-800">Service Status</span>
              </div>
              <p className="text-sm text-green-700 mt-1">Active & Scheduled</p>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-orange-600 mr-2" />
                <span className="text-sm font-medium text-orange-800">Schedule</span>
              </div>
              <p className="text-sm text-orange-700 mt-1">Daily at 10:00 PM IST</p>
            </div>
          </div>

          {/* Configuration Instructions */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Setup Instructions (One-time)</h3>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-start">
                <span className="bg-orange-100 text-orange-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium mr-3 flex-shrink-0 mt-0.5">1</span>
                <div>
                  <p className="font-medium text-gray-700">Create Free Twilio Account</p>
                  <p>Sign up at <span className="font-mono bg-gray-100 px-1 rounded">twilio.com/try-twilio</span> (Free tier: 1000 messages/month)</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <span className="bg-orange-100 text-orange-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium mr-3 flex-shrink-0 mt-0.5">2</span>
                <div>
                  <p className="font-medium text-gray-700">Configure Environment Variables</p>
                  <p>Add your Twilio credentials to the server's <span className="font-mono bg-gray-100 px-1 rounded">.env</span> file</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <span className="bg-orange-100 text-orange-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium mr-3 flex-shrink-0 mt-0.5">3</span>
                <div>
                  <p className="font-medium text-gray-700">Set Owner's WhatsApp Number</p>
                  <p>Add your WhatsApp number in the format: <span className="font-mono bg-gray-100 px-1 rounded">whatsapp:+91XXXXXXXXXX</span></p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handlePreviewReport}
              disabled={previewLoading}
              className="btn-secondary flex items-center justify-center"
            >
              <Eye className="h-4 w-4 mr-2" />
              {previewLoading ? 'Generating...' : 'Preview Report'}
            </button>
            
            <button
              onClick={handleTestReport}
              disabled={loading}
              className="btn-primary flex items-center justify-center"
            >
              <Send className="h-4 w-4 mr-2" />
              {loading ? 'Sending...' : 'Send Test Report'}
            </button>
          </div>

          {/* Cost Information */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-green-800 mb-1">
                  Completely Free Solution
                </h3>
                <p className="text-sm text-green-700">
                  • Twilio free tier: 1,000 WhatsApp messages per month<br/>
                  • Daily reports use only 30 messages per month<br/>
                  • No additional costs or subscriptions required
                </p>
              </div>
            </div>
          </div>
            </>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-96 overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Report Preview</h3>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
                  {reportPreview}
                </pre>
              </div>
              
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowPreview(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Database Backups Section */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <HardDrive className="h-6 w-6 mr-3 text-orange-600" />
            <h2 className="text-xl font-semibold text-gray-900">Database Backups</h2>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchBackups}
              disabled={backupsLoading}
              className="p-2 text-gray-500 hover:text-orange-600 hover:bg-gray-100 rounded-md transition-colors"
              title="Refresh backups list"
            >
              <RefreshCw className={`h-5 w-5 ${backupsLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleTriggerBackup}
              disabled={triggerBackupLoading}
              className="btn-primary flex items-center justify-center p-2 px-4 shadow-md sm:w-auto w-full transition-transform active:scale-95"
            >
              <Database className="h-4 w-4 mr-2" />
              {triggerBackupLoading ? 'Creating Backup...' : 'Backup Now'}
            </button>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-800 mb-1">
                Automated 30-Day Recovery System
              </h3>
              <p className="text-sm text-blue-700">
                Your entire database is securely backed up every single day at 1:00 AM automatically.
                To save space, older backups are gracefully pruned, retaining a rolling 30-day safety net.
              </p>
            </div>
          </div>
        </div>

        {/* Backups List */}
        <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
          {backupsLoading && backups.length === 0 ? (
            <div className="p-6 text-center text-gray-500">Loading backups...</div>
          ) : backups.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No backups found in the system yet. Creating one now!</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {backups.map((bkp, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {new Date(bkp.createdAt).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono text-xs">
                        {bkp.filename}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatBytes(bkp.size)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Environment Variables Guide */}
      <div className="card p-6">
        <div className="flex items-center mb-4">
          <AlertCircle className="h-5 w-5 mr-2 text-orange-600" />
          <h3 className="text-lg font-semibold text-gray-900">Environment Configuration</h3>
        </div>
        
        <div className="bg-gray-900 rounded-lg p-4 text-sm text-green-400 font-mono overflow-x-auto">
          <div className="text-gray-400 mb-2"># Add these to your server/.env file:</div>
          <div>TWILIO_ACCOUNT_SID=your_account_sid_here</div>
          <div>TWILIO_AUTH_TOKEN=your_auth_token_here</div>
          <div>TWILIO_WHATSAPP_NUMBER=whatsapp:+14155238886</div>
          <div>OWNER_WHATSAPP_NUMBER=whatsapp:+91XXXXXXXXXX</div>
        </div>
        
        <p className="text-sm text-gray-600 mt-3">
          Replace the placeholder values with your actual Twilio credentials and WhatsApp number.
        </p>
      </div>
    </div>
  );
};

export default Settings; 